import React, { useState } from 'react';
import { Card, Button, Row, Col, Typography, Tag, Space, Modal, Image, Divider } from 'antd';
import { CheckOutlined, EyeOutlined, StarOutlined } from '@ant-design/icons';
import { themePresets, getPresetsByCategory, getAllCategories, ThemePreset } from '../../data/themePresets';

const { Title, Text } = Typography;

interface ThemePresetSelectorProps {
  onSelect: (preset: ThemePreset) => void;
  onPreview: (preset: ThemePreset) => void;
  visible: boolean;
  onClose: () => void;
}

const ThemePresetSelector: React.FC<ThemePresetSelectorProps> = ({
  onSelect,
  onPreview,
  visible,
  onClose
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('全部');
  const categories = ['全部', ...getAllCategories()];
  const filteredPresets = selectedCategory === '全部' 
    ? themePresets 
    : getPresetsByCategory(selectedCategory);

  const handleSelect = (preset: ThemePreset) => {
    onSelect(preset);
    onClose();
  };

  const handlePreview = (preset: ThemePreset) => {
    onPreview(preset);
  };

  const renderPresetCard = (preset: ThemePreset) => (
    <Col xs={24} sm={12} md={8} lg={6} key={preset.name}>
      <Card
        hoverable
        style={{
          height: '100%',
          background: preset.variables.gradientBackground || preset.variables.backgroundColor,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
        bodyStyle={{
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)'
        }}
        actions={[
          <Button
            key="preview"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(preset)}
          >
            预览
          </Button>,
          <Button
            key="select"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => handleSelect(preset)}
          >
            选择
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              margin: '0 auto 12px',
              borderRadius: '12px',
              background: preset.variables.gradientPrimary || preset.variables.primaryColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {preset.name.charAt(0)}
          </div>
          <Title level={4} style={{ margin: 0, color: preset.variables.textColor }}>
            {preset.name}
          </Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {preset.description}
          </Text>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Tag color="blue" style={{ marginBottom: '8px' }}>
            {preset.category}
          </Tag>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              background: preset.variables.primaryColor,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
            title="主色调"
          />
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              background: preset.variables.secondaryColor,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
            title="辅助色"
          />
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              background: preset.variables.accentColor,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
            title="强调色"
          />
        </div>

        <div style={{ fontSize: '12px', color: preset.variables.textSecondaryColor }}>
          <div>圆角: {preset.variables.cardBorderRadius}</div>
          <div>字体: {preset.variables.fontSize}</div>
          {preset.variables.gradientPrimary && (
            <div>渐变: ✓</div>
          )}
          {preset.variables.backgroundImage && (
            <div>背景图: ✓</div>
          )}
        </div>
      </Card>
    </Col>
  );

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <StarOutlined />
          选择主题预设
          <Tag color="purple">NEW</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: '1200px' }}
      footer={null}
    >
      <div style={{ marginBottom: '24px' }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          选择一个预设主题作为起点，然后可以进一步自定义
        </Text>
        
        <Space wrap>
          {categories.map(category => (
            <Button
              key={category}
              type={selectedCategory === category ? 'primary' : 'default'}
              onClick={() => setSelectedCategory(category)}
              size="small"
            >
              {category}
            </Button>
          ))}
        </Space>
      </div>

      <Divider />

      <Row gutter={[16, 16]}>
        {filteredPresets.map(renderPresetCard)}
      </Row>

      {filteredPresets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">没有找到该分类的主题预设</Text>
        </div>
      )}
    </Modal>
  );
};

export default ThemePresetSelector;






